import { type ReactNode, type ReactElement } from 'react'
import { StoneRebateGraph } from '../rebate-graph/StoneRebateGraph'
import { StoneRebateType } from '@stuller/stullercom/data-access/apollo-queries'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { fas } from '@awesome.me/kit-3dbd93c064/icons'
import { type IconProp } from '@fortawesome/fontawesome-svg-core'

interface SerializedGemstoneSellingInfoProps {
  /**
   * Whether the user can view the diamond rebate
   */
  canViewGemstoneRebate?: boolean
}

interface SellingInfoItemProps {
  /**
   * The icon to display
   */
  icon: IconProp
  /**
   * The title of the selling info item
   */
  title: string
  /**
   * The description of the selling info item
   */
  description: ReactNode
}

/**
 * A selling information item for serialized diamonds
 */
function SellingInfoItem ({ icon, title, description }: SellingInfoItemProps): ReactElement {
  return (
    <div className='col-12 col-md-6 mb-5'>
      <div className='row'>
        <div className='col-auto'>
          <div className='rounded-circle bg-white d-flex align-items-center justify-content-center shadow-sm' style={{ width: 50, height: 50 }}>
            <FontAwesomeIcon icon={icon} size='2xl' className='text-blue-400' />
          </div>
        </div>
        <div className='col'>
          <p className='mb-0 fw-bold fs-3'>{title}</p>
          <p className='fs-4 mb-0'>{description}</p>
        </div>
      </div>
    </div>
  )
}

/**
 * The selling information for serialized gemstones
 */
function SerializedGemstoneSellingInfo ({ canViewGemstoneRebate }: SerializedGemstoneSellingInfoProps): ReactElement {
  return (
    <div className='py-6 py-md-8' style={{ background: 'var(--Gradients-EEE-FFF-, linear-gradient(0deg, #EEE 0%, #FFF 100%))' }}>
      <div className='container mw-xxl px-4'>
        <div className='row'>
          <div className='col-12'>
            <h2 className='h1 mb-5'>Notable Gems<sup>®</sup> come with</h2>
          </div>
          <div className='col-12 col-sm'>
            <div className='row'>
              <SellingInfoItem
                icon={fas.faCalendarDays}
                title='Easy Reviews & Returns'
                description='Qualified accounts enjoy 5-day reviews and prepaid returns.'
              />
              <SellingInfoItem
                icon={fas.faShieldCheck}
                title='Loss Protection for 1 Year'
                description='Give customers greater confidence to buy from you.'
              />
              <SellingInfoItem
                icon={fas.faHeart}
                title='Personalized Inscription'
                description='Engrave a personal message for as little as $25.00.'
              />
              <SellingInfoItem
                icon={fas.faCheck}
                title='Buy with Confidence'
                description='Each gemstone is hand selected and individually graded.'
              />
            </div>
          </div>
          {canViewGemstoneRebate != null && canViewGemstoneRebate && (
            <div className='col-12 col-md-auto order-4 mt-3 mt-md-0'>
              <div className='d-flex justify-content-center'>
                <StoneRebateGraph stoneType={StoneRebateType.Gemstone} />
              </div>
            </div>
          )}
        </div>
        <div className='row mt-5 mt-md-0'>
          <div className='col-12 col-lg-10'>
            <p className='text-start mb-0'>
              Grading reports are available on select stones.
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}

export {
  SerializedGemstoneSellingInfo
}
